import React, { useEffect, useState } from 'react';
import { Box, TextField, Button, Paper, Typography, Grid, Alert, Checkbox, FormControlLabel } from '@mui/material';
import { peopleService } from '../../services/api';
import ErrorBanner from '../ui/ErrorBanner';
import { useConfirm } from '../Common/ConfirmDialog';

export default function PersonForm({ person, disabled=false, onSaved }){
  const confirm = useConfirm();
  const personId = person?.Id ?? person?.id ?? null;
  const [form, setForm] = useState({ firstName:'', middleName:'', lastName:'', personalEmail:'', personalIdNumber:'', phone:'', linkedInProfileUrl:'', isActive:true });
  const [origName, setOrigName] = useState({ firstName:'', lastName:'' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(()=>{
    if (!personId) { setForm({ firstName:'', middleName:'', lastName:'', personalEmail:'', personalIdNumber:'', phone:'', linkedInProfileUrl:'', isActive:true }); setOrigName({ firstName:'', lastName:'' }); return; }
    // hydrate from possibly partial shape; prefer full keys if present
    const fn = person?.FirstName ?? person?.firstName ?? '';
    const mn = person?.MiddleName ?? person?.middleName ?? '';
    const ln = person?.LastName ?? person?.lastName ?? '';
    const pe = person?.PersonalEmail ?? person?.personalEmail ?? '';
    const pin = person?.PersonalIdNumber ?? person?.personalIdNumber ?? '';
    const ph = person?.Phone ?? person?.phone ?? '';
    const li = person?.LinkedInProfileUrl ?? person?.linkedInProfileUrl ?? '';
    const ia = person?.IsActive ?? person?.isActive ?? true;
    setForm({ firstName: fn, middleName: mn, lastName: ln, personalEmail: pe, personalIdNumber: pin, phone: ph, linkedInProfileUrl: li, isActive: ia });
    setOrigName({ firstName: fn, lastName: ln });
  }, [personId, person]);

  const onChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm(prev => ({ ...prev, [name]: type==='checkbox' ? checked : value }));
  };

  const doSave = async (e) => {
    e?.preventDefault?.();
    const willChangeName = (form.firstName?.trim() !== origName.firstName) || (form.lastName?.trim() !== origName.lastName);
    if (personId && willChangeName && (person?.hasEmployee || person?.hasAppUser)){
      const ok = await confirm({ message: 'Ще промените името на лице, което има AppUser/Employee. Продължавате ли?' });
      if (!ok) return;
    }
    setLoading(true); setError('');
    try{
      const payload = {
        firstName: form.firstName.trim(),
        middleName: form.middleName?.trim() || null,
        lastName: form.lastName.trim(),
        personalEmail: form.personalEmail?.trim() || null,
        personalIdNumber: form.personalIdNumber?.trim() || null,
        phone: form.phone?.trim() || null,
        linkedInProfileUrl: form.linkedInProfileUrl?.trim() || null,
        isActive: !!form.isActive
      };
      const res = personId
        ? await peopleService.update(personId, payload)
        : await peopleService.create(payload);

      // Update form with response data to ensure all fields are synced
      const fn = res?.FirstName ?? res?.firstName ?? form.firstName;
      const mn = res?.MiddleName ?? res?.middleName ?? form.middleName;
      const ln = res?.LastName ?? res?.lastName ?? form.lastName;
      const pe = res?.PersonalEmail ?? res?.personalEmail ?? form.personalEmail;
      const pin = res?.PersonalIdNumber ?? res?.personalIdNumber ?? form.personalIdNumber;
      const ph = res?.Phone ?? res?.phone ?? form.phone;
      const li = res?.LinkedInProfileUrl ?? res?.linkedInProfileUrl ?? form.linkedInProfileUrl;
      const ia = res?.IsActive ?? res?.isActive ?? form.isActive;
      setForm({ firstName: fn, middleName: mn, lastName: ln, personalEmail: pe, personalIdNumber: pin, phone: ph, linkedInProfileUrl: li, isActive: ia });
      setOrigName({ firstName: fn, lastName: ln });

      onSaved?.(res);
    }catch(err){
      setError(err?.response?.data || err.message);
    } finally { setLoading(false); }
  };

  const disabledAll = disabled;

  return (
    <Paper sx={{ p:2 }}>
      <Typography variant="h6" sx={{ mb:1 }}>Person</Typography>
      {error && <ErrorBanner message={error} onClose={()=>setError('')} />}
      <Box component="form" onSubmit={doSave}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}><TextField fullWidth label="First Name" name="firstName" value={form.firstName} onChange={onChange} required disabled={disabledAll} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Middle Name" name="middleName" value={form.middleName} onChange={onChange} disabled={disabledAll} /></Grid>
          <Grid item xs={12} sm={4}><TextField fullWidth label="Last Name" name="lastName" value={form.lastName} onChange={onChange} required disabled={disabledAll} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Personal Email" name="personalEmail" value={form.personalEmail} onChange={onChange} type="email" disabled={disabledAll} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Phone" name="phone" value={form.phone} onChange={onChange} disabled={disabledAll} /></Grid>
          <Grid item xs={12} sm={6}><TextField fullWidth label="Personal ID Number" name="personalIdNumber" value={form.personalIdNumber} onChange={onChange} disabled={disabledAll} /></Grid>

          <Grid item xs={12} sm={6}><TextField fullWidth label="LinkedIn URL" name="linkedInProfileUrl" value={form.linkedInProfileUrl} onChange={onChange} disabled={disabledAll} /></Grid>
          <Grid item xs={12} sm={6}><FormControlLabel control={<Checkbox name="isActive" checked={!!form.isActive} onChange={onChange} disabled={disabledAll} />} label="Active" /></Grid>
        </Grid>
        <Box sx={{ mt:2, display:'flex', gap:1 }}>
          <Button type="submit" variant="contained" disabled={disabledAll || loading}>{loading?'Saving...':'Save'}</Button>
        </Box>
      </Box>
    </Paper>
  );
}

